import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { FaExpandArrowsAlt } from 'react-icons/fa';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../styles/mapbox-popup-fix.css';
import '../styles/map-cursor-mix.css';
import '../styles/map-popups.css';
import PhotoModalWithAgent from '../components/PhotoModalWithAgent';
import AgentContactModal from '../components/AgentContactModal';
import { getGalleryImageUrl } from '../utils/imageUtils';
import { panToMarkerAndAddPopupOnMoveEnd } from '../utils/mobileMapInteraction';
import { animatePopupIn, setMarkerStacking } from '../utils/mapboxPopupUi';
import { useActiveListings } from '../services/activeListingsDataService';
import analyticsService from '../services/analyticsService';
import { getAddressParts } from '../utils/addressUtils';

// Global function to open agent modal from map popup
window.openAgentModalFromListingsMap = function(memberKey) {
  if (window.listingsMapAgentModal) {
    window.listingsMapAgentModal({ show: true, memberKey });
  }
};

// Custom hook to manage photo modal state globally across map markers
function usePhotoModalState() {
  const [activeModalListing, setActiveModalListing] = useState(null);
  
  const showModal = (listing) => {
    setActiveModalListing(listing);
  };
  
  const hideModal = () => {
    setActiveModalListing(null);
  };
  
  return {
    activeModalListing,
    showModal,
    hideModal
  };
}


// Helper function to create popup HTML content for listings
const createListingPopupHtml = (listing) => {
  const photoUrl = listing.photo || '';
  const price = listing.price ? `$${listing.price.toLocaleString()}` : 'Price unavailable';
  const beds = listing.beds ?? null;
  const baths = listing.baths ?? null;
  const sqft = listing.sqft != null ? listing.sqft.toLocaleString() : null;
  const addressParts = getAddressParts(listing);
  const address = addressParts.streetAddress;
  const cityStateZip = addressParts.cityStateZip;
  const agentName = listing.agentName || '';
  const listingId = listing.listingId || '';
  const neighborhood = (listing.subdivisionName && listing.subdivisionName !== 'N/A') ? listing.subdivisionName : (listing.city || '');
  const status = listing.status || '';
  const propertyType = listing.propertyType || '';
  const propertySubType = listing.propertySubType || '';
  const lotSizeAcres = listing.lotSizeAcres;

  const isLandListing = (() => {
    const type = (propertyType || '').toString().toLowerCase();
    const subType = (propertySubType || '').toString().toLowerCase();
    return type.includes('land') || type.includes('lot') || subType.includes('land') || subType.includes('lot');
  })();

  const isCommercialListing = (() => {
    const type = (propertyType || '').toString().toLowerCase();
    return type.includes('commercial');
  })();

  const formatAcresParts = (acres) => {
    if (acres == null) return null;
    const n = Number(acres);
    if (!Number.isFinite(n) || n <= 0) return null;
    const rounded = Math.round(n * 100) / 100;
    const valueText = rounded.toLocaleString(undefined, { maximumFractionDigits: 2 });
    const labelText = rounded === 1 ? 'acre' : 'acres';
    return { valueText, labelText };
  };
  
  // Camera icon and photo count
  const photoCount = Array.isArray(listing.photos) ? listing.photos.length : 0;
  const cameraIconSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="white" viewBox="0 0 16 16" style="margin-right: 4px;">
      <path d="M15 12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h1.172a3 3 0 0 0 2.12-.879l.83-.828A1 1 0 0 1 6.827 3h2.344a1 1 0 0 1 .707.293l.828.828A3 3 0 0 0 12.828 5H14a1 1 0 0 1 1 1v6zM2 4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1.172a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 9.172 2H6.828a2 2 0 0 0-1.414.586l-.828.828A2 2 0 0 1 3.172 4H2z"/>
      <path d="M8 11a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm0-4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z"/>
    </svg>
  `;
  const photoCountBadgeHtml = photoCount > 0 ? `
      <div 
        style="position: absolute; bottom: 8px; right: 8px; background-color: rgba(0, 0, 0, 0.6); color: white; padding: 4px 8px; border-radius: 4px; font-size: 12px; display: flex; align-items: center; z-index: 1; cursor: pointer;"
        data-photo-badge="true"
      >
        ${cameraIconSvg}${photoCount}
      </div>
  ` : '';

  return `
    <div style="
      width: 280px;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      overflow: hidden;
      background-color: #fff;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
      font-family: 'Montserrat', Arial, system-ui, sans-serif;
    ">
      <div style="position: relative; width: 100%; padding-bottom: 65%; margin: 0; background: #f0f0f0; overflow: hidden; border-radius: 8px 8px 0 0;">
        ${photoUrl ? `
          <img 
            src="${getGalleryImageUrl(photoUrl)}" 
            alt="${address}" 
            style="
              position: absolute;
              width: 100%;
              height: 100%;
              object-fit: cover;
              top: 0;
              left: 0;
              cursor: pointer;
              margin: 0;
              padding: 0;
              border: none;
              display: block;
            "
            class="map-popup-photo"
          />` : 
          `<div style="position: absolute; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #888; font-size: 14px;">No image</div>`
        }
        ${status ? `
          <div style="
            position: absolute;
            top: 8px;
            left: 8px;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: bold;
            background-color: rgba(0, 0, 0, 0.7);
            color: white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            z-index: 2;
            max-width: calc(100% - 16px);
          ">
            ${status}
          </div>
        ` : ''}
        ${photoCountBadgeHtml}
      </div>
      <div style="padding: 16px; display: flex; flex-direction: column; flex-grow: 1;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <div style="font-size: 22px; font-weight: 600; color: #333; font-variant-numeric: tabular-nums;">${price}</div>
          ${listingId ? `<div style="font-size: 11px; color: #555; margin-left: 10px; white-space: nowrap; line-height: 1;">MLS # ${listingId}</div>` : ''}
        </div>
        ${isLandListing || isCommercialListing ? (() => {
          const acres = formatAcresParts(lotSizeAcres);
          const pieces = [];
          if (acres) {
            pieces.push(`<span style="font-weight: 600;">${acres.valueText}</span>&nbsp;${acres.labelText}`);
          } else {
            pieces.push(`<span>--</span>`);
          }
          return `
            <div style="display: flex; align-items: center; color: #444; margin-bottom: 12px; height: 24px;">
              <div style="display: flex; align-items: center; font-size: 16px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                ${pieces.join('')}
              </div>
            </div>
          `;
        })() : `
          <div style="display: flex; align-items: center; color: #444; margin-bottom: 12px; height: 24px;">
            <div style="display: flex; align-items: center; font-size: 16px;">
              ${beds != null ? `<span><span style="font-weight: 600;">${beds}</span>&nbsp;bed</span>` : ''}
              ${(beds != null && baths != null) ? `<span style="margin: 0 4px; color: #555;">·</span>` : ''}
              ${baths != null ? `<span><span style="font-weight: 600;">${baths}</span>&nbsp;bath</span>` : ''}
              ${(baths != null && sqft != null) ? `<span style="margin: 0 4px; color: #555;">·</span>` : ''}
              ${sqft != null ? `<span><span style="font-weight: 600;">${sqft}</span>&nbsp;sqft</span>` : ''}
            </div>
          </div>
        `}
        <div style="margin-bottom: 8px; overflow: hidden;">
          <div style="font-size: 16px; font-weight: 500; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-align: left; direction: ltr;" title="${address}">
            ${address}
          </div>
          <div style="font-size: 14px; color: #666; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-align: left; direction: ltr;">
            ${cityStateZip}
          </div>
        </div>
        ${neighborhood ? `
          <div style="font-size: 14px; color: #555; display: flex; align-items: center; height: 24px; margin: 2px 0 0 0;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 4px; flex-shrink: 0;"><path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="#555"/></svg>
            <span style="word-wrap: break-word; overflow-wrap: break-word;">${neighborhood}</span>
          </div>
        ` : ''}
        ${agentName ? `
          <div style="font-size: 14px; color: #666; display: flex; align-items: center; height: 20px; margin-top: 4px;">
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg" 
              style="margin-right: 4px; flex-shrink: 0; cursor: ${listing.listAgentKey ? 'pointer' : 'default'}; transition: opacity 0.2s ease;"
              onclick="${listing.listAgentKey ? `openAgentModalFromListingsMap('${listing.listAgentKey}')` : ''}"
              ${listing.listAgentKey ? `
                onmouseover="this.style.opacity='0.7';"
                onmouseout="this.style.opacity='1';"
              ` : ''}
            >
              <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#666"/>
            </svg>
            <span 
              onclick="${listing.listAgentKey ? `openAgentModalFromListingsMap('${listing.listAgentKey}')` : ''}"
              style="word-wrap: break-word; overflow-wrap: break-word; cursor: ${listing.listAgentKey ? 'pointer' : 'default'}; color: #666; text-decoration: none; transition: color 0.2s ease;"
              ${listing.listAgentKey ? `
                onmouseover="this.style.color='#0043ff'; this.style.textDecoration='underline';"
                onmouseout="this.style.color='#666'; this.style.textDecoration='none';"
              ` : ''}
            >${agentName}</span>
          </div>
        ` : ''}
      </div>
    </div>
  `;
};

// Set Mapbox token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

function ActiveListingsMapView({ isMobile, isActive = true }) {
  const { listings, loading, error } = useActiveListings();
  const [hoveredMarkerId, setHoveredMarkerId] = useState(null);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const selectedMarkerIdRef = useRef(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  // Ensure we always do one initial auto-fit, even if the map emits early move events.
  const hasPerformedInitialFitRef = useRef(false);
  // Guard against accidental marker taps during pinch-zoom gestures on mobile.
  const suppressMarkerTapUntilRef = useRef(0);
  const isPinchGestureRef = useRef(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [selectedAgentKey, setSelectedAgentKey] = useState(null);
  
  // Keep ref in sync with state
  useEffect(() => {
    selectedMarkerIdRef.current = selectedMarkerId;
  }, [selectedMarkerId]);

  // Register global agent modal callback
  useEffect(() => {
    window.listingsMapAgentModal = ({ show, memberKey }) => {
      setShowAgentModal(show);
      setSelectedAgentKey(memberKey);
    };

    return () => {
      window.listingsMapAgentModal = null;
    };
  }, []);

  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const popupRef = useRef(null);
  const originalMapViewRef = useRef(null);
  const mapViewRootRef = useRef(null);
  const mapDomContainerRef = useRef(null);
  const { activeModalListing, showModal: originalShowModal, hideModal: originalHideModal } = usePhotoModalState();
  const isPhotoModalOpenRef = useRef(false);
  const savedCameraStateRef = useRef(null);

  const getValidPoints = useCallback((list) => {
    if (!Array.isArray(list)) return [];
    return list
      .map((listing) => {
        const lat = parseFloat(listing.latitude);
        const lon = parseFloat(listing.longitude);
        if (isNaN(lat) || isNaN(lon)) return null;
        if (lat === 0 && lon === 0) return null;
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
        return { lat, lon };
      })
      .filter(Boolean);
  }, []);

  /**
   * Fit bounds to the main local cohort while excluding true geographic outliers.
   *
   * Important: we avoid trimming by latitude/longitude percentiles because that can
   * accidentally remove legit “satellite” pockets (e.g., Warm Springs) when they
   * happen to be in the top tail.
   *
   * Strategy:
   * - Compute a robust center (median lat/lon)
   * - Compute distance (km) to center for each point
   * - Use a robust outlier cutoff on distances: \(Q3 + k \cdot IQR\)
   */
  const getTrimmedBounds = useCallback((points, fallbackBounds) => {
    if (!points?.length) return fallbackBounds;
    if (points.length < 10) return fallbackBounds;

    const sorted = (arr) => [...arr].sort((a, b) => a - b);
    const median = (arr) => {
      const s = sorted(arr);
      const n = s.length;
      if (n === 0) return NaN;
      const mid = Math.floor(n / 2);
      return n % 2 === 0 ? (s[mid - 1] + s[mid]) / 2 : s[mid];
    };
    const quantile = (arr, q) => {
      const s = sorted(arr);
      if (!s.length) return NaN;
      const pos = (s.length - 1) * q;
      const base = Math.floor(pos);
      const rest = pos - base;
      if (s[base + 1] == null) return s[base];
      return s[base] + rest * (s[base + 1] - s[base]);
    };

    const centerLat = median(points.map((p) => p.lat));
    const centerLon = median(points.map((p) => p.lon));
    if (![centerLat, centerLon].every(Number.isFinite)) return fallbackBounds;

    const toRad = (d) => (d * Math.PI) / 180;
    const haversineKm = (a, b) => {
      const R = 6371;
      const dLat = toRad(b.lat - a.lat);
      const dLon = toRad(b.lon - a.lon);
      const lat1 = toRad(a.lat);
      const lat2 = toRad(b.lat);
      const s =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
      return R * c;
    };

    const center = { lat: centerLat, lon: centerLon };
    const distancesKm = points.map((p) => haversineKm(center, p));
    if (!distancesKm.every(Number.isFinite)) return fallbackBounds;

    const q1 = quantile(distancesKm, 0.25);
    const q3 = quantile(distancesKm, 0.75);
    if (![q1, q3].every(Number.isFinite)) return fallbackBounds;

    const iqr = q3 - q1;
    if (!Number.isFinite(iqr) || iqr <= 0) {
      // No meaningful spread; prefer using all points rather than arbitrarily trimming.
      return fallbackBounds;
    }

    // Slightly more conservative than the classic 1.5 to avoid trimming legit satellites.
    const k = 2.0;
    const cutoffKm = q3 + k * iqr;

    const inliers = points.filter((_, idx) => distancesKm[idx] <= cutoffKm);
    if (inliers.length < 3) return fallbackBounds;

    const b = new mapboxgl.LngLatBounds();
    inliers.forEach(({ lat, lon }) => b.extend([lon, lat]));
    return b.isEmpty() ? fallbackBounds : b;
  }, []);

  // Function to get marker color based on hover and selection state
  const getMarkerColor = useCallback((markerId) => {
    const isSelected = selectedMarkerId === markerId;
    const isHovered = hoveredMarkerId === markerId;
    const isModalOpenForThisMarker = isPhotoModalOpenRef.current && 
      activeModalListing && 
      selectedMarkerId === markerId;
    
    const shouldIgnoreHover = isSelected;
    const color = (isSelected || (!shouldIgnoreHover && isHovered) || isModalOpenForThisMarker) ? '#0043ff' : '#ff1200';
    return color;
  }, [hoveredMarkerId, activeModalListing, selectedMarkerId]);

  // Function to update marker color
  const updateMarkerColor = useCallback((marker, markerId) => {
    const newColor = getMarkerColor(markerId);
    // Ensure selected/hovered marker stays above other markers.
    setMarkerStacking(marker, { isSelected: selectedMarkerId === markerId, isHovered: hoveredMarkerId === markerId });
    if (typeof marker.setColor === 'function') {
      marker.setColor(newColor);
      return;
    }

    const markerElement = marker.getElement();
    const svgElement = markerElement.querySelector('svg');
    if (svgElement) {
      const pathElement = svgElement.querySelector('path');
      if (pathElement) {
        pathElement.setAttribute('fill', newColor);
      }
    }
  }, [getMarkerColor, hoveredMarkerId, selectedMarkerId]);

  // Listings are already sorted by Days on Market from the service
  const sortedListings = useMemo(() => {
    if (!listings || !Array.isArray(listings)) return [];
    return listings;
  }, [listings]);

  // Wrapped modal handlers to track modal state
  const showModal = useCallback((listing) => {
    analyticsService.trackMapInteraction('Marker Photo Click', {
      propertyId: listing?.listingId || listing?.listingKey,
      address: listing?.address
    });
    
    if (mapRef.current) {
      savedCameraStateRef.current = {
        center: mapRef.current.getCenter(),
        zoom: mapRef.current.getZoom(),
        bearing: mapRef.current.getBearing(),
        pitch: mapRef.current.getPitch()
      };
      
      isPhotoModalOpenRef.current = true;
      originalShowModal(listing);
      
      requestAnimationFrame(() => {
        if (mapRef.current && savedCameraStateRef.current) {
          mapRef.current.jumpTo({
            center: savedCameraStateRef.current.center,
            zoom: savedCameraStateRef.current.zoom,
            bearing: savedCameraStateRef.current.bearing,
            pitch: savedCameraStateRef.current.pitch
          });
        }
      });
    } else {
      isPhotoModalOpenRef.current = true;
      originalShowModal(listing);
    }
  }, [originalShowModal]);

  const hideModal = useCallback(() => {
    originalHideModal();
    
    requestAnimationFrame(() => {
      if (mapRef.current && savedCameraStateRef.current) {
        mapRef.current.jumpTo({
          center: savedCameraStateRef.current.center,
          zoom: savedCameraStateRef.current.zoom,
          bearing: savedCameraStateRef.current.bearing,
          pitch: savedCameraStateRef.current.pitch
        });
      }
    });
    
    isPhotoModalOpenRef.current = false;
  }, [originalHideModal]);

  const setupPopupPhotoClickListener = useCallback((popupInstance, listingForModal) => {
    const popupElement = popupInstance.getElement();
    if (!popupElement) return;
    const photoImg = popupElement.querySelector('img.map-popup-photo');
    if (photoImg && !photoImg.dataset.listenerAttached) {
      const clickHandler = (e) => {
        e.stopPropagation();
        showModal(listingForModal);
      };
      photoImg.addEventListener('click', clickHandler);
      photoImg.dataset.listenerAttached = 'true';
    }
    
    const photoBadge = popupElement.querySelector('[data-photo-badge="true"]');
    if (photoBadge && !photoBadge.dataset.listenerAttached) {
      const badgeClickHandler = (e) => {
        e.stopPropagation();
        showModal(listingForModal);
      };
      photoBadge.addEventListener('click', badgeClickHandler);
      photoBadge.dataset.listenerAttached = 'true';
    }
  }, [showModal]);

  // Helper function to calculate popup anchor based on marker position
  const calculatePopupAnchor = useCallback((markerLngLat) => {
    if (!mapRef.current) return { anchor: 'top', offset: 25 };
    
    const map = mapRef.current;
    const mapContainer = map.getContainer();
    const mapRect = mapContainer.getBoundingClientRect();
    const markerPoint = map.project(markerLngLat);
    
    const CONTROL_BUFFER_TOP = 70;
    const CONTROL_BUFFER_RIGHT = 170;
    const CONTROL_BUFFER_BOTTOM = 30;
    const CONTROL_BUFFER_LEFT = 30;
    
    const estimatedPopupHeight = 400;
    const estimatedPopupWidth = 300;
    
    const centerX = mapRect.width / 2;
    const centerY = mapRect.height / 2;
    
    const spaceAbove = markerPoint.y - CONTROL_BUFFER_TOP;
    const spaceBelow = mapRect.height - markerPoint.y - CONTROL_BUFFER_BOTTOM;
    const spaceLeft = markerPoint.x - CONTROL_BUFFER_LEFT;
    const spaceRight = mapRect.width - markerPoint.x - CONTROL_BUFFER_RIGHT;
    
    const markerIsLeftOfCenter = markerPoint.x < centerX;
    const markerIsAboveCenter = markerPoint.y < centerY;
    
    let anchor, offset;
    
    if (markerIsAboveCenter && spaceBelow >= estimatedPopupHeight) {
      anchor = 'top';
      offset = 25;
    } else if (!markerIsAboveCenter && spaceAbove >= estimatedPopupHeight) {
      anchor = 'bottom';
      offset = [0, -25];
    } else if (markerIsLeftOfCenter && spaceRight >= estimatedPopupWidth) {
      anchor = 'left';
      offset = 25;
    } else if (!markerIsLeftOfCenter && spaceLeft >= estimatedPopupWidth) {
      anchor = 'right';
      offset = [-25, 0];
    } else if (spaceBelow >= estimatedPopupHeight) {
      anchor = 'top';
      offset = 25;
    } else if (spaceAbove >= estimatedPopupHeight) {
      anchor = 'bottom';
      offset = [0, -25];
    } else if (spaceRight >= estimatedPopupWidth) {
      anchor = 'left';
      offset = 25;
    } else if (spaceLeft >= estimatedPopupWidth) {
      anchor = 'right';
      offset = [-25, 0];
    } else {
      anchor = 'top';
      offset = 25;
    }
    
    return { anchor, offset };
  }, []);

  // Initialize map
  useEffect(() => {
    // If we're still loading, the map container isn't rendered yet (component returns early),
    // so wait until loading completes.
    if (loading) return;

    // Prefer the ref (more reliable than querying by id in React/StrictMode)
    const containerEl = mapDomContainerRef.current;
    if (!containerEl) {
      // In rare cases (StrictMode double-invoke / fast view switch), the ref may not be ready yet.
      // Bail out quietly; the effect will run again on re-mount.
      return;
    }

    if (mapRef.current) return;

    const map = new mapboxgl.Map({
      container: containerEl,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-121.3153, 44.0582],
      zoom: 10,
      attributionControl: true,
      scrollZoom: true,
      boxZoom: true,
      dragRotate: false,
      dragPan: true,
      keyboard: true,
      doubleClickZoom: true,
      touchZoomRotate: true
    });

    if (!isMobile) {
      const navControl = new mapboxgl.NavigationControl({ showCompass: false });
      map.addControl(navControl, 'bottom-right');
      
      setTimeout(() => {
        const zoomInButton = map.getContainer().querySelector('.mapboxgl-ctrl-zoom-in') || 
                            map.getContainer().querySelector('[aria-label="Zoom in"]') ||
                            map.getContainer().querySelector('button[title="Zoom in"]');
        const zoomOutButton = map.getContainer().querySelector('.mapboxgl-ctrl-zoom-out') || 
                             map.getContainer().querySelector('[aria-label="Zoom out"]') ||
                             map.getContainer().querySelector('button[title="Zoom out"]');
        
        if (zoomInButton) {
          zoomInButton.addEventListener('click', (e) => {
            setTimeout(() => {
              e.target.blur();
              e.target.style.outline = 'none';
            }, 0);
          });
        }
        
        if (zoomOutButton) {
          zoomOutButton.addEventListener('click', (e) => {
            setTimeout(() => {
              e.target.blur();
              e.target.style.outline = 'none';
            }, 0);
          });
        }
      }, 200);
    }
    
    mapRef.current = map;

    // NOTE: we intentionally do not gate taps on idle here; panning behavior should remain universal.

    // Gesture guard: if user is multi-touching (pinch) or the map begins zooming,
    // suppress marker "tap" selection for a short window.
    const suppressForMs = (ms) => {
      const until = Date.now() + ms;
      if (until > suppressMarkerTapUntilRef.current) {
        suppressMarkerTapUntilRef.current = until;
      }
    };

    const container = map.getContainer?.();
    const onTouchStartCapture = (e) => {
      if (e?.touches?.length >= 2) {
        isPinchGestureRef.current = true;
        suppressForMs(600);
      }
    };
    const onTouchMoveCapture = (e) => {
      if (e?.touches?.length >= 2) {
        isPinchGestureRef.current = true;
        suppressForMs(600);
      }
    };
    const onTouchEndCapture = (e) => {
      // Only apply the "tail" suppression if we were actually pinching.
      if (!isPinchGestureRef.current) return;
      if (e?.touches?.length >= 2) return;
      // If the pinch gesture just ended, keep suppression briefly so a trailing touchend
      // on a marker doesn't open a popup.
      suppressForMs(250);
      isPinchGestureRef.current = false;
    };

    if (container && typeof container.addEventListener === 'function') {
      container.addEventListener('touchstart', onTouchStartCapture, { passive: true, capture: true });
      container.addEventListener('touchmove', onTouchMoveCapture, { passive: true, capture: true });
      container.addEventListener('touchend', onTouchEndCapture, { passive: true, capture: true });
    }

    map.on('zoomstart', () => suppressForMs(600));

    map.on('click', () => {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      setHoveredMarkerId(null);
      setSelectedMarkerId(null);
      selectedMarkerIdRef.current = null;
    });

    map.on('zoom', () => setHasUserInteracted(true));
    map.on('pan', () => setHasUserInteracted(true));
    map.on('move', () => setHasUserInteracted(true));

    // Safety net: catch any stuck hover states when mouse moves over the map but not over a marker
    const onMapMouseMove = (e) => {
      const target = e.originalEvent?.target;
      const isOverMarker = target?.closest?.('.mapboxgl-marker');
      if (!isOverMarker) {
        setHoveredMarkerId((current) => current !== null ? null : current);
      }
    };
    map.on('mousemove', onMapMouseMove);

    return () => {
      map.off('mousemove', onMapMouseMove);
      if (container && typeof container.removeEventListener === 'function') {
        container.removeEventListener('touchstart', onTouchStartCapture, { capture: true });
        container.removeEventListener('touchmove', onTouchMoveCapture, { capture: true });
        container.removeEventListener('touchend', onTouchEndCapture, { capture: true });
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [isMobile, loading]);

  // Update markers when listings change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !sortedListings?.length) return;
    
    // Remove existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    
    const bounds = new mapboxgl.LngLatBounds();
    const points = [];
    
    sortedListings.forEach(listing => {
      const lat = parseFloat(listing.latitude);
      const lon = parseFloat(listing.longitude);
      if (isNaN(lat) || isNaN(lon)) return;
      // Ignore missing/invalid coords that often come through as 0,0
      if (lat === 0 && lon === 0) return;
      if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return;

      points.push({ lat, lon });
      bounds.extend([lon, lat]);

      const markerId = `marker-${listing.listingKey || `${lat}-${lon}`}`;
      const markerColor = '#ff1200';

      const marker = new mapboxgl.Marker({ color: markerColor, scale: 0.8 })
        .setLngLat([lon, lat]);
      
      marker.getElement().dataset.markerId = markerId;

      let popup;
      let desktopMoveHandler = null;
      
      if (isMobile) {
        popup = new mapboxgl.Popup({
          offset: 25,
          anchor: 'top',
          closeButton: false,
          closeOnClick: true,
          maxWidth: '280px',
          className: 'custom-mapbox-popup map-view-popup',
          closeOnMove: false
        });
      } else {
        const { anchor, offset } = calculatePopupAnchor([lon, lat]);
        
        popup = new mapboxgl.Popup({
          offset: offset,
          anchor: anchor,
          closeButton: false,
          closeOnClick: true,
          maxWidth: '280px',
          className: 'custom-mapbox-popup map-view-popup',
          closeOnMove: false
        });
      }
      
      popup
        .setLngLat([lon, lat])
        .setHTML(createListingPopupHtml(listing));
      
      popup.on('close', () => {
        // If we attached a desktop "out-of-view" handler, clean it up.
        if (desktopMoveHandler && mapRef.current) {
          mapRef.current.off('move', desktopMoveHandler);
          desktopMoveHandler = null;
        }

        if (
          isMobile &&
          originalMapViewRef.current &&
          mapRef.current &&
          !isPhotoModalOpenRef.current
        ) {
          mapRef.current.easeTo({
            center: originalMapViewRef.current.center,
            zoom: originalMapViewRef.current.zoom,
            duration: 500,
            essential: true
          });
          originalMapViewRef.current = null;
        }
        if (popupRef.current === popup) {
          popupRef.current = null;
        }
        if (selectedMarkerIdRef.current === markerId) {
          setSelectedMarkerId(null);
          selectedMarkerIdRef.current = null;
        }
        updateMarkerColor(marker, markerId);
      });

      marker.getElement().addEventListener('mouseenter', (e) => {
        e.stopPropagation();
        // Use the ref so this effect doesn't need to depend on selectedMarkerId (avoids rebuilding markers).
        if (!selectedMarkerIdRef.current) {
          setHoveredMarkerId(markerId);
          updateMarkerColor(marker, markerId);
        }
      });

      marker.getElement().addEventListener('mouseleave', (e) => {
        e.stopPropagation();
        setHoveredMarkerId(null);
        updateMarkerColor(marker, markerId);
      });

      // Fallback: pointerleave is more reliable than mouseleave during fast mouse movement
      marker.getElement().addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse') {
          setHoveredMarkerId(null);
          updateMarkerColor(marker, markerId);
        }
      });

      const handleMarkerInteraction = (e) => {
        e.stopPropagation();
        
        setHoveredMarkerId(null);
        
        // Use the ref so this effect doesn't need to depend on selectedMarkerId (avoids rebuilding markers).
        const isCurrentlySelected = selectedMarkerIdRef.current === markerId;
        
        analyticsService.trackMapInteraction('Marker Click', {
          propertyId: listing?.listingId || markerId,
          address: listing?.address
        });
        
        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }
        
        if (isCurrentlySelected) {
          setSelectedMarkerId(null);
          selectedMarkerIdRef.current = null;
          return;
        }
        
        setSelectedMarkerId(markerId);
        selectedMarkerIdRef.current = markerId;

        if (isMobile) {
          updateMarkerColor(marker, markerId);

          if (mapRef.current) {
            const openMobilePopup = () => {
              if (!mapRef.current) return;
            panToMarkerAndAddPopupOnMoveEnd({
              map: mapRef.current,
              popup,
              lngLat: { lon, lat },
              mapContainerEl: mapDomContainerRef.current,
              originalMapViewRef,
              addPopup: () => {
                if (!mapRef.current) return;
                popupRef.current = popup;
                popup.addTo(mapRef.current);
                setupPopupPhotoClickListener(popup, listing);
                animatePopupIn(popup);
              },
              // Use distance-based duration for consistent perceived motion speed across maps.
              opts: { fallbackOffsetY: -150 }
            });
            };

            openMobilePopup();
          }
        } else {
          // Desktop: match Open Houses feel with a subtle popup fade-in and auto-close if the marker goes out of view.
          popupRef.current = popup;
          popup.addTo(map);
          setupPopupPhotoClickListener(popup, listing);
          animatePopupIn(popup);

          desktopMoveHandler = () => {
            if (!popup.isOpen()) return;
            const mapInstance = mapRef.current;
            if (!mapInstance) return;

            const mapRect = mapInstance.getContainer().getBoundingClientRect();
            const markerPoint = mapInstance.project([lon, lat]);
            const buffer = 50;

            if (
              markerPoint.x < -buffer ||
              markerPoint.x > mapRect.width + buffer ||
              markerPoint.y < -buffer ||
              markerPoint.y > mapRect.height + buffer
            ) {
              const el = popup.getElement();
              if (el) {
                el.style.transition = 'opacity 0.3s ease-out';
                el.style.opacity = '0';
                setTimeout(() => {
                  popup.remove();
                }, 300);
              } else {
                popup.remove();
              }
              if (mapRef.current && desktopMoveHandler) {
                mapRef.current.off('move', desktopMoveHandler);
                desktopMoveHandler = null;
              }
            }
          };

          mapRef.current.on('move', desktopMoveHandler);
          updateMarkerColor(marker, markerId);
        }
      };

      if (isMobile) {
        marker.getElement().addEventListener('touchend', (e) => {
          e.preventDefault();
          e.stopPropagation();
          // Ignore marker taps that are part of (or immediately follow) a pinch zoom.
          if (Date.now() < suppressMarkerTapUntilRef.current) return;
          // If the map is currently moving/zooming (e.g. inertial pinch), don't treat this as a tap.
          if (mapRef.current?.isZooming?.() || mapRef.current?.isMoving?.()) return;
          handleMarkerInteraction(e);
        });
      } else {
        marker.getElement().addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          handleMarkerInteraction(e);
        });
      }

      marker.addTo(map);
      markersRef.current.push(marker);
    });
    
    // Auto-fit behavior:
    // - On the very first fit, SNAP to the computed bounds (duration 0) to avoid a visible
    //   "zooming out" animation on mount.
    // - After the first fit, respect user interactions and stop auto-fitting.
    const shouldAutoFit = !bounds.isEmpty() && (!hasPerformedInitialFitRef.current || !hasUserInteracted);
    if (shouldAutoFit) {
      const bottomPadding = isMobile ? 50 : 50;
      // Fit to the "bulk" of points so a few outliers don't overly zoom out.
      const targetBounds = getTrimmedBounds(points, bounds);

      const isInitialFit = !hasPerformedInitialFitRef.current;
      if (isInitialFit) {
        hasPerformedInitialFitRef.current = true;
      }

      map.fitBounds(targetBounds, { 
        padding: { top: 50, bottom: bottomPadding, left: 60, right: 60 },
        maxZoom: 13,
        // Snap on first mount so the map "renders correct" instead of animating from the default zoom.
        duration: isInitialFit ? 0 : 1000
      });
    }
  }, [sortedListings, showModal, isMobile, setupPopupPhotoClickListener, hasUserInteracted, calculatePopupAnchor, updateMarkerColor, getTrimmedBounds]);

  // Set initial marker colors after markers are created
  useEffect(() => {
    markersRef.current.forEach((marker) => {
      const markerElement = marker.getElement();
      const markerId = markerElement.dataset.markerId;
      if (markerId) {
        updateMarkerColor(marker, markerId);
      }
    });
  }, [markersRef.current.length, updateMarkerColor]);

  // Effect to update marker colors when any state changes
  useEffect(() => {
    markersRef.current.forEach((marker) => {
      const markerElement = marker.getElement();
      const markerId = markerElement.dataset.markerId;
      if (markerId) {
        updateMarkerColor(marker, markerId);
      }
    });
  }, [hoveredMarkerId, selectedMarkerId, activeModalListing, updateMarkerColor]);

  // Effect to handle map resizing
  useEffect(() => {
    const map = mapRef.current;
    const mapContainerElement = mapViewRootRef.current;

    if (!map || !mapContainerElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      map.resize();
    });

    resizeObserver.observe(mapContainerElement);

    requestAnimationFrame(() => {
      map.resize();
    });

    return () => {
      resizeObserver.unobserve(mapContainerElement);
      resizeObserver.disconnect();
    };
  }, []);

  // When the map becomes visible again (keep-alive), force a resize so tiles, hit-testing,
  // and marker interactions behave correctly.
  useEffect(() => {
    if (!isActive) return;
    const map = mapRef.current;
    if (!map) return;
    requestAnimationFrame(() => {
      try {
        map.resize();
      } catch {
        // Defensive: resize can throw if map is mid-destroy; ignore.
      }
    });
  }, [isActive]);

  const handleRecenterMap = () => {
    const map = mapRef.current;
    if (!map || !sortedListings || sortedListings.length === 0) {
      return;
    }

    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    setSelectedMarkerId(null);
    selectedMarkerIdRef.current = null;
    setHoveredMarkerId(null);
    markersRef.current.forEach((marker) => {
      const markerElement = marker.getElement();
      const markerId = markerElement?.dataset?.markerId;
      if (markerId) {
        updateMarkerColor(marker, markerId);
      }
    });

    const points = getValidPoints(sortedListings);
    const bounds = new mapboxgl.LngLatBounds();
    points.forEach(({ lat, lon }) => bounds.extend([lon, lat]));

    if (!bounds.isEmpty()) {
      const padding = { top: 50, bottom: 50, left: 60, right: 60 };
      // Use the SAME trimmed bounds behavior as initial load (not "fit all")
      const targetBounds = getTrimmedBounds(points, bounds);
      map.fitBounds(targetBounds, { 
        padding: padding,
        maxZoom: 13, 
        duration: 1000 
      });
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        fontSize: '16px',
        color: '#666'
      }}>
        Loading active listings...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        fontSize: '16px',
        color: '#d32f2f'
      }}>
        Error loading listings: {error.message}
      </div>
    );
  }

  if (!sortedListings || sortedListings.length === 0) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        fontSize: '16px',
        color: '#666'
      }}>
        No active listings found
      </div>
    );
  }

  return (
    <>
      <div ref={mapViewRootRef} className="map-side" style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        <div 
          ref={mapDomContainerRef}
          id="active-listings-map-view" 
          className="map-container"
          style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%'
          }}
        />
        {/* Map Controls */}
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 1600,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          alignItems: 'flex-end'
        }}>
          {/* Recenter Button */}
          <button
            onClick={e => { 
              handleRecenterMap(); 
              setTimeout(() => {
                if (e.currentTarget) {
                  e.currentTarget.blur();
                  e.currentTarget.style.outline = 'none';
                }
              }, 0);
            }}
            style={{
              background: 'white',
              border: '1px solid #dbdbdb',
              borderRadius: '4px',
              padding: '8px',
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: '1',
              outline: 'none'
            }}
            onMouseDown={(e) => e.preventDefault()}
            title="Recenter Map"
          >
            <FaExpandArrowsAlt size={18} color="#333" />
          </button>
        </div>
      </div>
      
      {/* Photo Modal */}
      {activeModalListing && (
        <PhotoModalWithAgent
          listing={activeModalListing}
          onClose={hideModal}
          isMobile={isMobile}
        />
      )}

      {/* Agent Contact Modal */}
      {showAgentModal && (
        <AgentContactModal
          memberKey={selectedAgentKey}
          className="map-agent-contact"
          onClose={() => {
            setShowAgentModal(false);
            setSelectedAgentKey(null);
          }}
          show={showAgentModal}
          isMobile={isMobile}
        />
      )}
    </>
  );
}

export default ActiveListingsMapView;
